import { formatPrice } from 'src/utils/currency'
import type { Currency } from '../api/types'

export type PackageCardMeta = {
  packNameI18nKey?: string
  packName?: string
  valueRatioPercent?: number
  totalValueTextI18nKey?: string
  totalValueText?: string
  /**
   * Raw approximate-image count for the popup chip — e.g. 100 / 1200.
   * Mirrors `packageCardMarketingMetadataSchema.equivalentImages` on the
   * contract (see `packages/pixai-contract/src/payment/extraPackageSku.ts`).
   */
  equivalentImages?: number
  /** i18n key override; resolves with `{ count: equivalentImages }`. */
  equivalentImagesTextI18nKey?: string
  /** Pre-formatted text fallback. */
  equivalentImagesText?: string
}

export type PackageContentMeta = {
  hideInCard?: boolean
  displayNameI18nKey?: string
  displayName?: string
  label?: string
  name?: string
}

export type PackageMarketingMetadata = PackageCardMeta & {
  card?: PackageCardMeta
}

export function formatMultiplierNumber(valueRatioPercent: number) {
  const multiplier = valueRatioPercent / 100
  if (!Number.isFinite(multiplier) || multiplier <= 0) return null
  const roundedToOneDecimal = Math.round(multiplier * 10) / 10
  return Number.isInteger(roundedToOneDecimal)
    ? String(roundedToOneDecimal)
    : roundedToOneDecimal.toFixed(1)
}

export function getCardMeta(
  metadata?: PackageMarketingMetadata | null,
): PackageCardMeta {
  return metadata?.card ?? metadata ?? {}
}

type NewbiePackOfferTier = {
  itemId: string
  purchased: boolean
  purchasable: boolean
}

type NewbiePackOfferPackage = {
  id: string
  legacyItemId?: string
  oneTimePurchase?: boolean
  prices: Partial<Record<Currency, number>>
  marketingMetadata?: PackageMarketingMetadata | null
}

export function getNewbiePackOfferSummary(args: {
  tiers?: NewbiePackOfferTier[] | null
  packs?: NewbiePackOfferPackage[] | null
  currency: Currency
}) {
  const eligibleIds = new Set(
    args.tiers
      ?.filter(tier => tier.purchasable && !tier.purchased)
      .map(tier => tier.itemId) ?? [],
  )

  const candidatePacks = (args.packs ?? []).filter(pack => {
    if (!pack.oneTimePurchase) return false
    if (eligibleIds.size === 0) return true
    return (
      eligibleIds.has(pack.id) ||
      (pack.legacyItemId != null && eligibleIds.has(pack.legacyItemId))
    )
  })

  const minPrice = candidatePacks.reduce<number | null>((current, pack) => {
    const price = pack.prices[args.currency]
    if (price == null || !Number.isFinite(price)) return current
    return current == null ? price : Math.min(current, price)
  }, null)

  const maxValueRatioPercent = candidatePacks.reduce<number | null>(
    (current, pack) => {
      const ratio = getCardMeta(pack.marketingMetadata).valueRatioPercent
      if (ratio == null || !Number.isFinite(ratio) || ratio <= 0) {
        return current
      }
      return current == null ? ratio : Math.max(current, ratio)
    },
    null,
  )

  return {
    priceText: minPrice == null ? null : formatPrice(args.currency, minPrice),
    multiplierText:
      maxValueRatioPercent == null
        ? null
        : formatMultiplierNumber(maxValueRatioPercent),
  }
}
